import React, { useState, useRef, useEffect, useContext } from 'react';
import { Container, Form, Button, Card, Badge, Modal } from 'react-bootstrap';
import {
  FaHeadset, FaTicketAlt, FaClock, FaTag, FaCalendarAlt,
  FaAlignLeft, FaTimes, FaPaperPlane, FaChevronLeft, FaComments
} from 'react-icons/fa';
import "../../../assets/css/Customer/CustomerSettings/CustomerHelpCenter.css";
import CustomerSettingsMasterLayout from '../../../layouts/Customer/CustomerSettingsMasterLayout';
import AppContext from "../../../context/AppContext";
import axios from 'axios';

const CustomerHelpCenter = () => {
  const { baseUrl, authToken } = useContext(AppContext);
  const [activeTab, setActiveTab] = useState('create');
  const [ticketForm, setTicketForm] = useState({ subject: '', category: '', description: '' });
  const [tickets, setTickets] = useState([]);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [conversationId, setConversationId] = useState(null); 
  const [modalView, setModalView] = useState('details');
  const [chatInput, setChatInput] = useState('');
  const [chatMessages, setChatMessages] = useState({});
  const chatEndRef = useRef(null);

  const categories = ['Payment', 'Ride', 'Technical', 'Account', 'Driver', 'Other'];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setTicketForm(prev => ({ ...prev, [name]: value }));
  };

  const fetchAllTickets = () => {
    const formData = new FormData();
    formData.append("tdcustomer_id", JSON.parse(localStorage.getItem('tdcustomer')).id);
    axios.post(`${baseUrl}/customer/helpsupport/fetchAllTickets`, formData, {
      headers: { Authorization: `Bearer ${authToken}` }
    }).then((res) => {
      if (res.data.success === true) setTickets(res.data.tickets);
    }).catch((error) => console.log(error));
  };

  useEffect(() => {
    fetchAllTickets();
  }, []);

  const handleSubmitTicket = () => {
    if (!ticketForm.subject || !ticketForm.category || !ticketForm.description) {
      alert('Please fill all fields');
      return;
    }
    const formData = new FormData();
    formData.append("tdcustomer_id", JSON.parse(localStorage.getItem('tdcustomer')).id);
    formData.append("subject", ticketForm.subject);
    formData.append("category", ticketForm.category);
    formData.append("description", ticketForm.description);
    formData.append("status", "Open");
    formData.append("raisedby", "customer");

    axios.post(`${baseUrl}/customer/helpsupport/createTicket`, formData, {
      headers: { "Content-Type": "multipart/form-data", Authorization: `Bearer ${authToken}` }
    }).then((res) => {
      if (res.data.success === true) {
        alert('Ticket submitted successfully!');
        setTicketForm({ subject: '', category: '', description: '' });
        fetchAllTickets();
        setActiveTab('tickets');
      }
    }).catch(() => alert("Network Error!"));
  };

  const handleViewDetails = (ticket) => {
    setSelectedTicket(ticket);
    setModalView('details');
    setShowDetailModal(true);

    const formData = new FormData();
    formData.append("tdhelpsupport_id", ticket.id);
    axios.post(`${baseUrl}/customer/helpsupport/fetchConversation`, formData, {
      headers: { Authorization: `Bearer ${authToken}` }
    }).then((res) => {
      console.log(res.data);
      if (res.data.success === true) {
        setConversationId(res.data.tdconversation.id); 
        const msgs = res.data.tdconversation.tdchats?.map(chat => ({ 
          id: chat.id,
          sender: chat.Sender === 'tdcustomer' ? 'user' : 'support',
          text: chat.message,
          time: new Date(chat.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        })) || [];
        setChatMessages(prev => ({ ...prev, [ticket.id]: msgs }));
      }
    }).catch((error) => console.log(error));
  };

  const handleCloseModal = () => {
    setShowDetailModal(false);
    setModalView('details');
  };

  const handleSendMessage = () => {
    if (!chatInput.trim() || !selectedTicket) return;

    const formData = new FormData();
    formData.append("tdconversation_id", conversationId); // ✅
    formData.append("message", chatInput.trim());
    formData.append("type", "text");
    formData.append("Sender", "tdcustomer");

    axios.post(`${baseUrl}/customer/helpsupport/sendMessage`, formData, {
      headers: { Authorization: `Bearer ${authToken}` }
    }).then((res) => {
      if (res.data.success === true) {
        const newMsg = {
          id: Date.now(),
          sender: 'user',
          text: chatInput.trim(),
          time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        };
        setChatMessages(prev => ({
          ...prev,
          [selectedTicket.id]: [...(prev[selectedTicket.id] || []), newMsg]
        }));
        setChatInput('');
      }
    }).catch(() => alert("Network Error!"));
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  useEffect(() => {
    if (modalView === 'chat') {
      chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [chatMessages, modalView]);

  const getStatusBadge = (status) => (
    <Badge className="customerhelpcenter-status-badge">{status}</Badge>
  );

  const currentMessages = selectedTicket ? (chatMessages[selectedTicket.id] || []) : [];

  return (
    <CustomerSettingsMasterLayout screenName={"CustomerHelpCenter"} centered={true}>
      <div className="customerhelpcenter-help-center-container">
        <Container className="customerhelpcenter-container">

          {/* Tabs */}
          <div className="customerhelpcenter-tabs">
            <button
              className={`customerhelpcenter-tab ${activeTab === 'create' ? 'customerhelpcenter-tab-active' : ''}`}
              onClick={() => setActiveTab('create')}
            >
              <FaHeadset className="customerhelpcenter-tab-icon" /> Create Ticket
            </button>
            <button
              className={`customerhelpcenter-tab ${activeTab === 'tickets' ? 'customerhelpcenter-tab-active' : ''}`}
              onClick={() => setActiveTab('tickets')}
            >
              <FaTicketAlt className="customerhelpcenter-tab-icon" /> My Tickets ({tickets.length})
            </button>
          </div>

          {/* Create Ticket Form */}
          {activeTab === 'create' && (
            <div className="customerhelpcenter-form-section">
              <div className="customerhelpcenter-form-header">
                <div className="customerhelpcenter-help-icon-wrapper">
                  <FaHeadset className="customerhelpcenter-help-icon" />
                </div>
                <h6 className="customerhelpcenter-form-title">Submit a Ticket</h6>
                <p className="customerhelpcenter-form-description">
                  Please describe your issue and we'll get back to you as soon as possible
                </p>
              </div>

              <Form.Group className="customerhelpcenter-form-group">
                <Form.Label className="customerhelpcenter-form-label">Subject</Form.Label>
                <Form.Control type="text" name="subject" value={ticketForm.subject}
                  onChange={handleInputChange} placeholder="Brief description of your issue"
                  className="customerhelpcenter-form-input" />
              </Form.Group>

              <Form.Group className="customerhelpcenter-form-group">
                <Form.Label className="customerhelpcenter-form-label">Category</Form.Label>
                <Form.Select name="category" value={ticketForm.category}
                  onChange={handleInputChange} className="customerhelpcenter-form-input">
                  <option value="">Select a category</option>
                  {categories.map((cat, index) => (
                    <option key={index} value={cat}>{cat}</option>
                  ))}
                </Form.Select>
              </Form.Group>

              <Form.Group className="customerhelpcenter-form-group">
                <Form.Label className="customerhelpcenter-form-label">Description</Form.Label>
                <Form.Control as="textarea" rows={8} name="description"
                  value={ticketForm.description} onChange={handleInputChange}
                  placeholder="Please provide detailed information about your issue..."
                  className="customerhelpcenter-form-textarea" />
              </Form.Group>

              <Button className="customerhelpcenter-submit-btn" onClick={handleSubmitTicket}>
                Submit Ticket
              </Button>
            </div>
          )}

          {/* Tickets List */}
          {activeTab === 'tickets' && (
            <div className="customerhelpcenter-tickets-section">
              {tickets.length === 0 ? (
                <div className="customerhelpcenter-no-tickets">
                  <FaTicketAlt className="customerhelpcenter-no-tickets-icon" />
                  <p className="customerhelpcenter-no-tickets-text">No tickets found</p>
                  <p className="customerhelpcenter-no-tickets-subtext">Create a ticket to get help</p>
                </div>
              ) : (
                <div className="customerhelpcenter-tickets-list">
                  {tickets.map((ticket) => (
                    <Card key={ticket.id} className="customerhelpcenter-ticket-card">
                      <Card.Body className="customerhelpcenter-ticket-body">
                        <div className="customerhelpcenter-ticket-header">
                          <div className="customerhelpcenter-ticket-number">
                            <FaTicketAlt className="customerhelpcenter-ticket-icon" />
                            <span>TKT-{String(ticket.id).padStart(3, '0')}</span> {/* ✅ */}
                          </div>
                          {getStatusBadge(ticket.status)}
                        </div>
                        <h6 className="customerhelpcenter-ticket-subject">{ticket.subject}</h6>
                        <div className="customerhelpcenter-ticket-meta">
                          <span className="customerhelpcenter-ticket-category">
                            <strong>Category:</strong> {ticket.category}
                          </span>
                          <span className="customerhelpcenter-ticket-date">
                            <FaClock className="customerhelpcenter-date-icon" />
                            {new Date(ticket.created_at).toLocaleDateString()} {/* ✅ */}
                          </span>
                        </div>
                        <p className="customerhelpcenter-ticket-description">{ticket.description}</p>
                        <div className="customerhelpcenter-ticket-footer">
                          <Button className="customerhelpcenter-view-btn" size="sm"
                            onClick={() => handleViewDetails(ticket)}>
                            View Details
                          </Button>
                        </div>
                      </Card.Body>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          )}

        </Container>
      </div>

      {/* Modal */}
      {selectedTicket && (
        <Modal show={showDetailModal} centered onHide={handleCloseModal}
          className="customerhelpcenter-detail-modal">

          <div className="customerhelpcenter-modal-header">
            <div className="customerhelpcenter-modal-header-left">
              {modalView === 'chat' && (
                <button className="customerhelpcenter-modal-back-btn"
                  onClick={() => setModalView('details')}>
                  <FaChevronLeft />
                </button>
              )}
              <div className="customerhelpcenter-modal-ticket-icon-wrap"><FaTicketAlt /></div>
              <div>
                <p className="customerhelpcenter-modal-ticket-number">
                  TKT-{String(selectedTicket.id).padStart(3, '0')} {/* ✅ */}
                </p>
                <p className="customerhelpcenter-modal-ticket-subject">{selectedTicket.subject}</p>
              </div>
            </div>
            <button className="customerhelpcenter-modal-close-btn" onClick={handleCloseModal}>
              <FaTimes />
            </button>
          </div>

          {modalView === 'details' && (
            <>
              <div className="customerhelpcenter-modal-body">
                <div className="customerhelpcenter-modal-status-row">
                  <span className="customerhelpcenter-modal-label">Status</span>
                  <Badge className="customerhelpcenter-status-badge" style={{ backgroundColor: '#1B9B82' }}>
                    {selectedTicket.status}
                  </Badge>
                </div>
                <div className="customerhelpcenter-modal-divider" />
                <div className="customerhelpcenter-modal-info-row">
                  <div className="customerhelpcenter-modal-info-icon-wrap"><FaTag /></div>
                  <div>
                    <p className="customerhelpcenter-modal-info-label">Category</p>
                    <p className="customerhelpcenter-modal-info-value">{selectedTicket.category}</p>
                  </div>
                </div>
                <div className="customerhelpcenter-modal-info-row">
                  <div className="customerhelpcenter-modal-info-icon-wrap"><FaCalendarAlt /></div>
                  <div>
                    <p className="customerhelpcenter-modal-info-label">Submitted On</p>
                    <p className="customerhelpcenter-modal-info-value">
                      {new Date(selectedTicket.created_at).toLocaleDateString()} {/* ✅ */}
                    </p>
                  </div>
                </div>
                <div className="customerhelpcenter-modal-divider" />
                <div className="customerhelpcenter-modal-description-section">
                  <div className="customerhelpcenter-modal-desc-header">
                    <FaAlignLeft className="customerhelpcenter-modal-desc-icon" />
                    <span className="customerhelpcenter-modal-label">Description</span>
                  </div>
                  <p className="customerhelpcenter-modal-desc-text">{selectedTicket.description}</p>
                </div>
                <div className={`customerhelpcenter-modal-status-box customerhelpcenter-modal-status-box--${selectedTicket.status.toLowerCase().replace(' ', '-')}`}>
                  {selectedTicket.status === 'Open' && <p>⏳ Your ticket is open and will be reviewed by our support team shortly.</p>}
                  {selectedTicket.status === 'In Progress' && <p>🔧 Our team is actively working on your issue.</p>}
                  {selectedTicket.status === 'Resolved' && <p>✅ This ticket has been resolved.</p>}
                </div>
              </div>
              <div className="customerhelpcenter-modal-footer customerhelpcenter-modal-footer--two-btn">
                <Button className="customerhelpcenter-modal-chat-btn" onClick={() => setModalView('chat')}>
                  <FaComments style={{ marginRight: 8 }} /> Chat with Support
                </Button>
                <Button className="customerhelpcenter-modal-close-footer-btn" onClick={handleCloseModal}>
                  Close
                </Button>
              </div>
            </>
          )}

          {modalView === 'chat' && (
            <div className="customerhelpcenter-chat-wrapper">
              <div className="customerhelpcenter-chat-messages">
                {currentMessages.length === 0 ? (
                  <div className="customerhelpcenter-chat-empty">
                    <div className="customerhelpcenter-chat-empty-icon"><FaComments /></div>
                    <p className="customerhelpcenter-chat-empty-title">No messages yet</p>
                    <p className="customerhelpcenter-chat-empty-sub">Send a message to start chatting with support</p>
                  </div>
                ) : (
                  currentMessages.map((msg) => (
                    <div key={msg.id}
                      className={`customerhelpcenter-chat-bubble-wrap ${msg.sender === 'user' ? 'customerhelpcenter-chat-bubble-wrap--user' : 'customerhelpcenter-chat-bubble-wrap--support'}`}>
                      {msg.sender === 'support' && <div className="customerhelpcenter-chat-avatar">S</div>}
                      <div className={`customerhelpcenter-chat-bubble ${msg.sender === 'user' ? 'customerhelpcenter-chat-bubble--user' : 'customerhelpcenter-chat-bubble--support'}`}>
                        <p className="customerhelpcenter-chat-bubble-text">{msg.text}</p>
                        <span className="customerhelpcenter-chat-bubble-time">{msg.time}</span>
                      </div>
                    </div>
                  ))
                )}
                <div ref={chatEndRef} />
              </div>
              <div className="customerhelpcenter-chat-input-area">
                <textarea className="customerhelpcenter-chat-input" placeholder="Type your message..."
                  value={chatInput} onChange={(e) => setChatInput(e.target.value)}
                  onKeyDown={handleKeyDown} rows={1} />
                <button className="customerhelpcenter-chat-send-btn" onClick={handleSendMessage}
                  disabled={!chatInput.trim()}>
                  <FaPaperPlane />
                </button>
              </div>
            </div>
          )}

        </Modal>
      )}
    </CustomerSettingsMasterLayout>
  );
};

export default CustomerHelpCenter;